import { useState } from "react";
import { createPortal } from "react-dom";
import { XStack, YStack, Circle, Text, SizableText } from "tamagui";
import { RiPencilLine } from "react-icons/ri";
import { UserProfile } from "../accountInterface";
import { useUser } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import api from "@services/axios/apiManager";
import { appToast } from "@components/Toast";
import { useHazePage, v2 } from "@screen/Home/pages/GasShop/skin";

interface UserProfileHeaderProps {
  user: UserProfile | null | any;
}

export default function UserProfileHeader({ user }: UserProfileHeaderProps) {
  // 淡霧頁：頁首底色是極淡的藍白，白字讀不到 → 招呼語改深藍、鉛筆鈕改淡藍底
  const haze = useHazePage();
  const { userData, setUserData } = useUser();
  const { cusJwtToken } = useCusAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const openEdit = () => {
    setDraft(user?.name || "");
    setEditing(true);
  };

  const save = async () => {
    const name = draft.trim();
    if (!name) {
      appToast.error("請輸入姓名");
      return;
    }
    if (name === (user?.name || "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      // TODO(後端)：更新客戶姓名 API（正式後端尚未提供時，練習模式由假後端處理；上線前需接真實端點）
      await api.put("/app-api/customer/profile", { customer_name: name }, { headers: { Authorization: `Bearer ${cusJwtToken}` } });
      setUserData({ ...(userData || {}), name, customer_name: name });
      appToast.success("姓名已更新");
      setEditing(false);
    } catch (e) {
      console.error("update customer name error", e);
      appToast.error("更新失敗，請稍後再試");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <XStack ai="center" gap="$3.5">
        {/* 使用者資訊 */}
        <YStack f={1} miw={0}>
          <XStack ai="center" gap="$2">
            {/* 粉圓只有 400 一個字重，fontWeight 800 只是瀏覽器合成的假粗體，
                在 $8 這種大字級下看起來偏細。補一道同色描邊把筆畫加厚——
                這是唯一不換字體就能真正變粗的做法。 */}
            <SizableText
              color={haze ? v2.hazeInk : "white"}
              size="$8"
              fontWeight="800"
              style={{ WebkitTextStroke: "0.7px currentColor" }}>
              Hi, {user?.name}
            </SizableText>
            {/* 編輯姓名 */}
            <Circle
              size={26}
              bg={haze ? "rgba(18,68,126,0.10)" : "rgba(255,255,255,0.2)"}
              cursor="pointer"
              pressStyle={{ opacity: 0.7, scale: 0.94 }}
              onPress={openEdit}>
              <RiPencilLine color={haze ? v2.hazeInk : "#fff"} size={14} />
            </Circle>
          </XStack>
          <Text color={haze ? v2.hazeSub : "rgba(255,255,255,0.75)"} fontSize={12} fontWeight="bold">
            歡迎回來
          </Text>
        </YStack>

      </XStack>

      {editing &&
        createPortal(
          <div
            onClick={() => !saving && setEditing(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 100050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ width: "100%", maxWidth: 360, background: "#fff", borderRadius: 18, padding: "20px 18px 18px", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#1f2937", marginBottom: 4 }}>編輯姓名</div>
              <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 14 }}>這個名字會顯示在帳號與訂單上</div>
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                }}
                maxLength={20}
                placeholder="請輸入姓名"
                style={{ width: "100%", boxSizing: "border-box", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "12px 14px", fontSize: 16, outline: "none", color: "#1f2937" }}
              />
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setEditing(false)}
                  style={{ flex: 1, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", borderRadius: 12, padding: "12px 8px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                  取消
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={save}
                  style={{ flex: 1, border: "none", background: "#2196f3", color: "#fff", borderRadius: 12, padding: "12px 8px", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
                  {saving ? "儲存中…" : "儲存"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
